import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { NotificationType } from "../../../constants/Enums";
import { registerToastTrigger } from "../../../services/notifications/toastBus";

/**
 * Optional extras for a toast (additive — every existing call passes none and
 * behaves exactly as before). Used by voice edit's "Price updated · Undo".
 */
export interface NotificationOptions {
  /** Button text shown in the toast, e.g. "Undo". Needs onAction. */
  actionLabel?: string;
  /** Runs when the action button is tapped; the toast closes. */
  onAction?: () => void;
  /** How long the toast stays, in ms. Default 3600. */
  durationMs?: number;
}

interface NotificationContextProps {
  isNotificationOpen: boolean;
  notificationMessage: string;
  notificationType: NotificationType;
  /** Options of the toast currently shown; null for a plain toast. */
  notificationOptions: NotificationOptions | null;
  /** Increments on every toast — lets the popup restart its animation for an action toast. */
  notificationId: number;
  triggerNotification: (message: string, type: NotificationType, options?: NotificationOptions) => void;
  /** Closes the current toast now. */
  dismissNotification: () => void;
  /** The current visibility state of the overlay. */
  showLoadingOverlay: boolean;
  /** The message currently displayed in the overlay. */
  loadingMessage: string;
  /** Function to immediately start the loading process with a message. */
  startLoading: (message: string) => void;
  /** Function to stop the loading process (handles any fade-out logic internally). */
  stopLoading: () => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined,
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState<NotificationType>(
    NotificationType.SUCCESS,
  );
  const [notificationOptions, setNotificationOptions] = useState<NotificationOptions | null>(null);
  const [notificationId, setNotificationId] = useState(0);
  // Latest toast id, and the id of the latest toast that carries options. A
  // plain toast's timer still closes whatever is showing (unchanged
  // behaviour), EXCEPT a newer options toast — otherwise an earlier "Saved"
  // toast's 3.6 s timer would cut an 8 s Undo toast short.
  const toastIdRef = useRef(0);
  const optionsToastIdRef = useRef(0);

  // State to control the loading overlay visibility
  const [showLoadingOverlay, setShowLoadingOverlay] = useState<boolean>(false);
  // State to control the message displayed in the overlay
  const [loadingMessage, setLoadingMessage] = useState<string>("Loading...");

  const triggerNotification = (
    message: string,
    type: NotificationType,
    options?: NotificationOptions,
  ) => {
    const id = ++toastIdRef.current;
    if (options) optionsToastIdRef.current = id;
    setNotificationMessage(message);
    setNotificationType(type);
    setNotificationOptions(options ?? null);
    setNotificationId(id);
    setIsNotificationOpen(true);

    // Automatically close the notification after 3.6 seconds (or the
    // options' duration)
    setTimeout(() => {
      if (options ? toastIdRef.current !== id : optionsToastIdRef.current > id) return;
      setIsNotificationOpen(false);
      setNotificationMessage("");
    }, options?.durationMs ?? 3600);
  };

  const dismissNotification = () => {
    toastIdRef.current += 1;
    setIsNotificationOpen(false);
    setNotificationMessage("");
  };

  // Expose the toast trigger to non-React callers (FCM push handler, native
  // bridge) via the toast bus.
  useEffect(() => {
    registerToastTrigger(triggerNotification);
    return () => registerToastTrigger(null);
  }, []);

  const startLoading = (loadingMessage: string) => {
    setShowLoadingOverlay(true);
    setLoadingMessage(loadingMessage);
  };

  const stopLoading = () => {
    setShowLoadingOverlay(false);
  };

  //  const startLoading = useCallback((message: string) => {
  //   setLoadingMessage(message);
  //   setIsLoading(true);
  // }, []);

  // const stopLoading = useCallback(() => {
  //   // 1. Visually update the message to indicate completion (optional, but good UX)
  //   setLoadingMessage('Complete. Finalizing...');

  //   // 2. Wait for the fade-out period, then set isLoading to false
  //   setTimeout(() => {
  //     setIsLoading(false);
  //   }, fadeOutDelayMs);
  // }, [fadeOutDelayMs]);

  // const contextValue = {
  //   isLoading,
  //   loadingMessage,
  //   startLoading,
  //   stopLoading,
  // };

  return (
    <NotificationContext.Provider
      value={{
        isNotificationOpen,
        notificationMessage,
        notificationType,
        notificationOptions,
        notificationId,
        triggerNotification,
        dismissNotification,
        showLoadingOverlay,
        loadingMessage,
        startLoading,
        stopLoading,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
